import * as THREE from "three";
import { Cell, RoadMap } from "@highmarch/domain";
import { Contact, GroundContacts, stampContacts } from "./groundSlope";
import { GroundHoles } from "./groundHoles";

/**
 * The ground's splat (groundMesh.ts's fifth layer): the road, its worn shoulder and the road card's preview painted
 * from the road spline, the flagstone pads under towers, and the contact shadows stamped into the slope map.
 */
export class GroundSplat extends GroundHoles {
  protected splatCanvas: HTMLCanvasElement | null = null;
  /** The splat's painted road/shoulder/preview channels, kept so a tower change can rewrite only the pad channel. */
  protected splatRGB: Uint8ClampedArray | null = null;
  /** Tower cells that get a flagstone pad and a contact shadow, and a cheap key of them to skip unchanged calls. */
  protected pads: Cell[] = [];
  protected padKey = -1;
  /** The contact registry version the occlusion channel was last stamped from. */
  protected stampedVersion = -1;
  /** Cells of the road card being placed (from the current open end), painted as road; null clears it. */
  protected preview: readonly Cell[] | null = null;
  protected lastRoad: RoadMap | null = null;
  /**
   * Trodden-earth patches (the worn shoulder's channel) round a point, world cells: the hub's war camp paints its
   * yards and tent rows with them (hubCamp.ts). Set before `build`; empty on a level.
   */
  worn: readonly { readonly x: number; readonly z: number; readonly r: number }[] = [];
  protected previewKey = "";
  /** Splat pixels per cell. 16 made the road edge soft by construction; at 32 the kerb can be a line. */
  static readonly SplatPx = 32;
  /** Width and blur of the road stroke and its worn shoulder, in cells (terrain F3). */
  static readonly RoadWidth = 0.86;
  static readonly RoadBlur = 0.11;
  static readonly ShoulderWidth = 1.1;
  static readonly ShoulderBlur = 0.3;
  /** The trodden-earth patch under a tower (groundShader.ts, through the worn layer), in cells: solid out to `PadInner`, gone by `PadOuter`. */
  static readonly PadInner = 0.4;
  static readonly PadOuter = 0.56;

  /** How hard a tower darkens the ground round its plinth, and how far: anchors' half-width × 1.35 (lighting F4b). */
  static readonly TowerContact = { r: 0.62, s: 0.5 };

  /**
   * Tower cells that stand on a flagstone pad with a contact shadow round it (terrain F3, F4). Call whenever the sim
   * may have built or sold one; unchanged sets return at once without allocating, so it is safe to call per tick.
   * The fortress is left out: its footprint's contact shadow is static (GridView registers it).
   */
  setTowers(slots: readonly { readonly alive: boolean; readonly cell: Cell }[]): void {
    let key = 0;
    for (const t of slots) {
      if (!t.alive || this.grid.isBaseCell(t.cell)) continue;
      key = (key * 31 + (t.cell.x + 1) * 977 + (t.cell.y + 1) * 131) >>> 0;
    }
    if (key === this.padKey) return;
    this.padKey = key;
    this.pads = slots.filter((t) => t.alive && !this.grid.isBaseCell(t.cell)).map((t) => t.cell);
    this.writePads();
    this.restamp();
  }

  /**
   * Rewrites the slope map's occlusion channel from the horizon AO, the registered props and the towers. Positions
   * are in world space and the map is in the ground's UNWARPED space, so each is pulled back through the lateral
   * wander first — at a gorge lip the wander is a fifth of a cell, and the shadow would otherwise miss the trunk.
   */
  protected restamp(): void {
    if (!this.slope) return;
    this.stampedVersion = GroundContacts.version;
    const all: Contact[] = [];
    const unwarp = (c: Contact): Contact => { const [dx, dz] = this.warpAt(c.x, c.z); return { ...c, x: c.x - dx, z: c.z - dz }; };
    for (const c of GroundContacts.list) all.push(unwarp(c));
    const T = GroundSplat.TowerContact;
    for (const p of this.pads) all.push(unwarp({ x: p.x, z: p.y, r: T.r, s: T.s }));
    stampContacts(this.slope, all);
  }

  setPreview(cells: readonly Cell[] | null): void {
    const key = cells ? cells.map((c) => `${c.x},${c.y}`).join(";") : "";
    if (key === this.previewKey) return;
    this.previewKey = key;
    this.preview = cells;
    this.paintSplat(this.lastRoad);
  }
  /**
   * Red = road, green = worn dirt shoulder, blue = the road card's preview, alpha = the flagstone pads under towers.
   * The first three are drawn from the road spline on a canvas so the track is continuous; the canvas is then read
   * back and uploaded as a data texture with the pads written into alpha by hand (a canvas's alpha is premultiplied,
   * which would destroy the colour channels wherever a pad was not).
   */
  protected paintSplat(road: RoadMap | null): void {
    this.lastRoad = road;
    const cv = this.splatCanvas;
    if (!cv) return;
    const px = GroundSplat.SplatPx;
    const W = this.grid.width * px, H = this.grid.height * px;
    if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
    const g = cv.getContext("2d", { willReadFrequently: true })!;
    g.clearRect(0, 0, W, H);
    g.fillStyle = "#000"; g.fillRect(0, 0, W, H);
    const at = (c: Cell) => [(c.x + 0.5) * px, (c.y + 0.5) * px] as const;
    if (this.preview && this.preview.length > 1) {
      // the road card's preview, in blue: same stroke as the road so it is the road it will become
      g.save();
      g.globalCompositeOperation = "lighter";
      g.filter = `blur(${px * GroundSplat.RoadBlur}px)`;
      g.strokeStyle = "#0000ff"; g.lineWidth = px * GroundSplat.RoadWidth; g.lineCap = "round"; g.lineJoin = "round";
      g.beginPath();
      this.preview.forEach((c, i) => { const [x, y] = at(c); if (i === 0) g.moveTo(x, y); else g.lineTo(x, y); });
      g.stroke();
      g.restore();
    }
    for (const w of this.worn) {
      // a soft disc of worn earth: full at the middle, gone at its rim
      const [x, y] = [(w.x + 0.5) * px, (w.z + 0.5) * px], r = w.r * px;
      const grad = g.createRadialGradient(x, y, r * 0.25, x, y, r);
      grad.addColorStop(0, "#00ff00"); grad.addColorStop(1, "#000000");
      g.save(); g.globalCompositeOperation = "lighter"; g.fillStyle = grad;
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); g.restore();
    }
    if (road) {
      const cells = road.roadCells.filter((c) => !this.terrain.bridgeAt(c));
      // shoulder first (green), then the road itself (red) over it; "lighter" so the red stroke does not erase the
      // shoulder under it — the shader needs to know the shoulder runs on under the kerb
      const strokes = [["#00ff00", GroundSplat.ShoulderWidth, GroundSplat.ShoulderBlur], ["#ff0000", GroundSplat.RoadWidth, GroundSplat.RoadBlur]] as const;
      for (const [colour, width, blur] of strokes) {
        g.save();
        g.globalCompositeOperation = "lighter";
        g.filter = `blur(${px * blur}px)`;
        g.strokeStyle = colour; g.lineWidth = px * width; g.lineCap = "round"; g.lineJoin = "round";
        g.beginPath();
        let started = false;
        for (let i = 0; i < road.roadCells.length; i++) {
          const c = road.roadCells[i];
          if (!cells.includes(c)) { started = false; continue; }
          const [x, y] = at(c);
          if (!started) { g.moveTo(x, y); started = true; } else g.lineTo(x, y);
        }
        g.stroke();
        g.restore();
      }
    }
    this.splatRGB = g.getImageData(0, 0, W, H).data;
    if (!this.splat || this.splat.image.width !== W || this.splat.image.height !== H) {
      this.splat?.dispose();
      this.splat = new THREE.DataTexture(new Uint8Array(W * H * 4), W, H, THREE.RGBAFormat, THREE.UnsignedByteType);
      this.splat.magFilter = this.splat.minFilter = THREE.LinearFilter;
      this.splat.wrapS = this.splat.wrapT = THREE.ClampToEdgeWrapping;
      this.splat.flipY = false;
      if (this.splatUniform) this.splatUniform.value = this.splat;
    }
    this.writePads();
  }
  /** Copies the painted channels into the splat texture and writes the tower pads into its alpha. */
  protected writePads(): void {
    const tex = this.splat, rgb = this.splatRGB;
    if (!tex || !rgb) return;
    const data = tex.image.data as Uint8Array;
    data.set(rgb);
    const px = GroundSplat.SplatPx, W = tex.image.width, H = tex.image.height;
    for (let i = 3; i < data.length; i += 4) data[i] = 0;
    const inner = GroundSplat.PadInner, outer = GroundSplat.PadOuter;
    for (const c of this.pads) {
      const cx = (c.x + 0.5) * px, cz = (c.y + 0.5) * px, R = Math.ceil(outer * px);
      for (let y = Math.max(0, Math.floor(cz - R)); y <= Math.min(H - 1, Math.ceil(cz + R)); y++) {
        for (let x = Math.max(0, Math.floor(cx - R)); x <= Math.min(W - 1, Math.ceil(cx + R)); x++) {
          const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cz) / px;
          if (d >= outer) continue;
          const t = d <= inner ? 1 : 1 - (d - inner) / (outer - inner);
          const o = (y * W + x) * 4 + 3;
          data[o] = Math.max(data[o], Math.round(t * t * (3 - 2 * t) * 255));
        }
      }
    }
    tex.needsUpdate = true;
  }
}
